import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import DashboardLayout from '../components/DashboardLayout';
import DualLocationCard from '../components/DualLocationCard';
import { toast } from '../components/ui/sonner';
import {
  ArrowLeft, Wrench, Gauge, History, ClipboardCheck, Plus, Trash2,
  AlertTriangle, CheckCircle, User, Calendar, X, Fuel, Satellite, FileText,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_PILL = {
  active: 'bg-green-100 text-green-700',
  in_shop: 'bg-amber-100 text-amber-700',
  retired: 'bg-slate-200 text-slate-600',
};

const fmtDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString(); } catch { return iso; }
};
const fmtDT = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
};

const AssetDetail = () => {
  const { assetId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const isAdmin = user?.role === 'company_admin' || user?.role === 'super_admin';
  const canManageStatus = isAdmin || user?.role === 'dispatcher';

  const [asset, setAsset] = useState(null);
  const [drivers, setDrivers] = useState([]);
  const [rules, setRules] = useState([]);
  const [logs, setLogs] = useState([]);
  const [odoLogs, setOdoLogs] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [tab, setTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [detailsForm, setDetailsForm] = useState(null);
  const [savingDetails, setSavingDetails] = useState(false);
  const [ocrDoc, setOcrDoc] = useState(null);

  const readExpiryAI = async (d) => {
    setOcrDoc(d.doc_type);
    try {
      const r = await axios.post(`${API}/assets/${assetId}/ocr-expiry?doc_type=${encodeURIComponent(d.doc_type)}`, {}, { withCredentials: true });
      const fresh = await axios.get(`${API}/assets/${assetId}`, { withCredentials: true });
      setAsset(fresh.data);
      toast.success(`Read ${d.doc_type === 'physical_damage_insurance' ? 'insurance' : 'registration'} expiry: ${r.data.expiry_date}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not read expiry from document');
    } finally {
      setOcrDoc(null);
    }
  };

  useEffect(() => {
    if (asset) {
      setDetailsForm({
        asset_number: asset.asset_number || '',
        make: asset.make || '',
        model: asset.model || '',
        year: asset.year || '',
        vin: asset.vin || '',
        license_plate: asset.license_plate || '',
        license_plate_state: asset.license_plate_state || '',
        registration_expiry: asset.registration_expiry || '',
        insurance_expiry: asset.insurance_expiry || '',
      });
    }
  }, [asset]);

  const saveDetails = async (markReviewed) => {
    if (!detailsForm) return;
    setSavingDetails(true);
    try {
      const payload = {
        ...detailsForm,
        year: detailsForm.year ? parseInt(detailsForm.year, 10) : null,
      };
      if (markReviewed) payload.pending_admin_review = false;
      await axios.put(`${API}/assets/${assetId}`, payload, { withCredentials: true });
      const r = await axios.get(`${API}/assets/${assetId}`, { withCredentials: true });
      setAsset(r.data);
      toast.success(markReviewed ? 'Asset marked reviewed' : 'Asset details saved');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to save');
    } finally {
      setSavingDetails(false);
    }
  };

  const expiryWarn = (dateStr, label) => {
    if (!dateStr) return null;
    const d = new Date(dateStr);
    if (isNaN(d)) return null;
    const days = Math.ceil((d - new Date()) / 86400000);
    if (days < 0) return `${label} EXPIRED ${Math.abs(days)} day(s) ago`;
    if (days <= 30) return `${label} expires in ${days} day(s)`;
    return null;
  };

  const downloadAppDoc = async (d) => {
    try {
      const r = await axios.get(`${process.env.REACT_APP_BACKEND_URL}${d.download_url}`, { withCredentials: true });
      const { file_data, content_type, file_name } = r.data;
      const href = String(file_data || '').startsWith('data:')
        ? file_data
        : `data:${content_type || 'application/octet-stream'};base64,${file_data}`;
      const a = document.createElement('a');
      a.href = href;
      a.download = file_name || d.file_name || 'document';
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch {
      toast.error('Could not open document');
    }
  };

  const [showRuleForm, setShowRuleForm] = useState(false);
  const [ruleForm, setRuleForm] = useState({ name: '', interval_miles: '', interval_days: '', last_performed_miles: '', last_performed_at: '' });
  const [showLogForm, setShowLogForm] = useState(false);
  const [logForm, setLogForm] = useState({ description: '', performed_at_miles: '', cost: '', vendor: '', notes: '', rule_id: '' });
  const [fuelLogs, setFuelLogs] = useState([]);
  const [fuelSummary, setFuelSummary] = useState(null);
  const [showFuelForm, setShowFuelForm] = useState(false);
  const [fuelForm, setFuelForm] = useState({ odometer: '', gallons: '', price_per_gallon: '', station: '', fuel_type: 'diesel', is_full_tank: true, notes: '' });

  useEffect(() => {
    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assetId]);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [, rRes, lRes, oRes, hRes, dRes, fRes, fsRes] = await Promise.all([
        axios.get(`${API}/assets/${assetId}/maintenance-status`, { withCredentials: true }).then(() => null).catch(() => null), // warm-up no-op
        axios.get(`${API}/maintenance-rules?asset_id=${assetId}`, { withCredentials: true }),
        axios.get(`${API}/maintenance-logs?asset_id=${assetId}`, { withCredentials: true }),
        axios.get(`${API}/odometer-logs?asset_id=${assetId}`, { withCredentials: true }),
        axios.get(`${API}/assets/${assetId}/assignment-history`, { withCredentials: true }).catch(() => ({ data: [] })),
        isAdmin ? axios.get(`${API}/drivers`, { withCredentials: true }) : Promise.resolve({ data: [] }),
        axios.get(`${API}/fuel-logs?asset_id=${assetId}`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API}/fuel-logs/summary?asset_id=${assetId}`, { withCredentials: true }).catch(() => ({ data: null })),
      ]);
      const assetRes = await axios.get(`${API}/assets/${assetId}`, { withCredentials: true });
      setAsset(assetRes.data);
      setRules(rRes.data);
      setLogs(lRes.data);
      setOdoLogs(oRes.data);
      setAssignments(hRes.data);
      setDrivers((dRes.data || []).filter(d => d.status === 'active'));
      setFuelLogs(fRes.data || []);
      setFuelSummary(fsRes.data);
    } catch (e) {
      console.error('Failed to load asset', e);
    } finally {
      setLoading(false);
    }
  };

  const computeRuleStatus = (r) => {
    const odo = asset?.current_odometer || 0;
    let milesRemaining = null, isOverdue = false, isDue = false;
    if (r.interval_miles) {
      const last = r.last_performed_miles || 0;
      milesRemaining = (last + r.interval_miles) - odo;
      if (milesRemaining <= 500) isDue = true;
      if (milesRemaining < 0) isOverdue = true;
    }
    let daysRemaining = null;
    if (r.interval_days) {
      if (r.last_performed_at) {
        const next = new Date(r.last_performed_at);
        next.setDate(next.getDate() + r.interval_days);
        daysRemaining = Math.round((next - new Date()) / (1000 * 60 * 60 * 24));
        if (daysRemaining <= 14) isDue = true;
        if (daysRemaining < 0) isOverdue = true;
      } else {
        daysRemaining = 0;
        isDue = true;
      }
    }
    return { milesRemaining, daysRemaining, isOverdue, isDue };
  };

  const handleAssign = async (driverId) => {
    try {
      await axios.post(`${API}/assets/${assetId}/assign`, { driver_id: driverId || null }, { withCredentials: true });
      await fetchAll();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to assign');
    }
  };

  const handleStatus = async (status) => {
    try {
      await axios.patch(`${API}/assets/${assetId}/status`, { status }, { withCredentials: true });
      await fetchAll();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to update status');
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Delete this asset? This cannot be undone.')) return;
    try {
      await axios.delete(`${API}/assets/${assetId}`, { withCredentials: true });
      navigate('/assets');
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to delete');
    }
  };

  const handleCreateRule = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        asset_id: assetId,
        name: ruleForm.name,
        interval_miles: ruleForm.interval_miles ? parseFloat(ruleForm.interval_miles) : null,
        interval_days: ruleForm.interval_days ? parseInt(ruleForm.interval_days, 10) : null,
        last_performed_miles: ruleForm.last_performed_miles ? parseFloat(ruleForm.last_performed_miles) : null,
        last_performed_at: ruleForm.last_performed_at || null,
      };
      await axios.post(`${API}/maintenance-rules`, payload, { withCredentials: true });
      setRuleForm({ name: '', interval_miles: '', interval_days: '', last_performed_miles: '', last_performed_at: '' });
      setShowRuleForm(false);
      await fetchAll();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to create rule');
    }
  };

  const handleDeleteRule = async (ruleId) => {
    if (!window.confirm('Delete this maintenance rule?')) return;
    try {
      await axios.delete(`${API}/maintenance-rules/${ruleId}`, { withCredentials: true });
      await fetchAll();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to delete rule');
    }
  };

  const handleCreateLog = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        asset_id: assetId,
        description: logForm.description,
        performed_at_miles: logForm.performed_at_miles ? parseFloat(logForm.performed_at_miles) : null,
        cost: logForm.cost ? parseFloat(logForm.cost) : null,
        vendor: logForm.vendor || null,
        notes: logForm.notes || null,
        rule_id: logForm.rule_id || null,
      };
      await axios.post(`${API}/maintenance-logs`, payload, { withCredentials: true });
      setLogForm({ description: '', performed_at_miles: '', cost: '', vendor: '', notes: '', rule_id: '' });
      setShowLogForm(false);
      await fetchAll();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to log maintenance');
    }
  };

  const handleCreateFuel = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        asset_id: assetId,
        odometer: parseFloat(fuelForm.odometer),
        gallons: parseFloat(fuelForm.gallons),
        price_per_gallon: fuelForm.price_per_gallon ? parseFloat(fuelForm.price_per_gallon) : null,
        station: fuelForm.station || null,
        fuel_type: fuelForm.fuel_type,
        is_full_tank: fuelForm.is_full_tank,
        notes: fuelForm.notes || null,
      };
      await axios.post(`${API}/fuel-logs`, payload, { withCredentials: true });
      setFuelForm({ odometer: '', gallons: '', price_per_gallon: '', station: '', fuel_type: 'diesel', is_full_tank: true, notes: '' });
      setShowFuelForm(false);
      await fetchAll();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to log fuel');
    }
  };

  if (loading || !asset) {
    return <DashboardLayout><div className="p-8" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Loading...</div></DashboardLayout>;
  }

  const overdueCount = rules.filter(r => computeRuleStatus(r).isOverdue).length;
  const dueCount = rules.filter(r => { const s = computeRuleStatus(r); return s.isDue && !s.isOverdue; }).length;

  return (
    <DashboardLayout>
      <div className="p-6 md:p-8 max-w-6xl mx-auto" data-testid="asset-detail-page">
        <button
          data-testid="back-to-assets"
          onClick={() => navigate('/assets')}
          className="flex items-center gap-2 text-sm text-slate-600 hover:text-[#002FA7] mb-4"
          style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
        >
          <ArrowLeft className="w-4 h-4" /> Back to assets
        </button>

        {/* Header */}
        <div className="bg-white border border-slate-200 rounded-md p-6 mb-6">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-widest text-slate-500" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                {asset.asset_type.toUpperCase()}{asset.year ? ` • ${asset.year}` : ''}
              </p>
              <h1 className="text-3xl font-bold tracking-tight text-slate-900 mt-1" style={{ fontFamily: 'Work Sans, sans-serif' }}>{asset.label}</h1>
              <p className="text-sm text-slate-600 mt-1" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>{[asset.make, asset.model].filter(Boolean).join(' ') || asset.serial_number || '—'}</p>
            </div>
            <div className="flex items-center gap-2">
              <span className={`px-3 py-1 rounded text-xs font-semibold uppercase tracking-wider ${STATUS_PILL[asset.status]}`}>{asset.status === 'in_shop' ? 'In Shop' : asset.status}</span>
              {isAdmin && (
                <button data-testid="delete-asset" onClick={handleDelete} className="p-2 rounded-md text-red-600 hover:bg-red-50 transition-all">
                  <Trash2 className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6 pt-6 border-t border-slate-100">
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-widest text-slate-500" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Current Odometer</p>
              <p data-testid="asset-odometer" className="text-2xl font-bold text-slate-900 mt-1" style={{ fontFamily: 'Work Sans, sans-serif' }}>{Math.round(asset.current_odometer || 0).toLocaleString()} <span className="text-sm font-normal text-slate-500">mi</span></p>
            </div>
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-widest text-slate-500" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Driver</p>
              <p className="text-base font-semibold text-slate-900 mt-1 truncate" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>{asset.assigned_driver_name || <span className="text-slate-400 italic font-normal">Unassigned</span>}</p>
            </div>
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-widest text-slate-500" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Maintenance</p>
              <p className="text-base font-semibold mt-1" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                {overdueCount > 0 ? <span className="text-red-700">{overdueCount} overdue</span> : dueCount > 0 ? <span className="text-amber-700">{dueCount} due soon</span> : <span className="text-green-700">All current</span>}
              </p>
            </div>
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-widest text-slate-500" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Created</p>
              <p className="text-base font-semibold text-slate-900 mt-1" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>{fmtDate(asset.created_at)}</p>
            </div>
          </div>

          {canManageStatus && (
            <div className="flex flex-wrap items-center gap-3 mt-4 pt-4 border-t border-slate-100">
              {isAdmin && (
                <div className="flex items-center gap-2">
                  <label className="text-xs font-semibold uppercase tracking-widest text-slate-500" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Assign to:</label>
                  <select
                    data-testid="assign-driver-select"
                    value={asset.assigned_driver_id || ''}
                    onChange={(e) => handleAssign(e.target.value)}
                    className="px-3 py-1.5 border border-slate-200 rounded-md text-sm bg-white"
                  >
                    <option value="">— Unassigned —</option>
                    {drivers.map(d => <option key={d.user_id} value={d.user_id}>{d.name || d.email}</option>)}
                  </select>
                </div>
              )}
              <div className="flex items-center gap-2">
                <label className="text-xs font-semibold uppercase tracking-widest text-slate-500" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Status:</label>
                <select data-testid="change-status-select" value={asset.status} onChange={(e) => handleStatus(e.target.value)} className="px-3 py-1.5 border border-slate-200 rounded-md text-sm bg-white">
                  <option value="active">Active</option>
                  <option value="in_shop">In Shop</option>
                  <option value="retired">Retired</option>
                </select>
              </div>
            </div>
          )}
        </div>

        {/* Complete-this-asset card (admins) — finish driver-submitted details + asset number */}
        {isAdmin && detailsForm && (
          <div data-testid="asset-complete-card" className={`rounded-md border p-5 mb-6 ${asset.pending_admin_review ? 'border-amber-300 bg-amber-50' : 'border-slate-200 bg-white'}`}>
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-bold text-slate-900" style={{ fontFamily: 'Work Sans, sans-serif' }}>
                {asset.pending_admin_review ? 'Finish this asset (submitted by driver)' : 'Asset details'}
              </h3>
              {asset.pending_admin_review && (
                <span className="px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider bg-amber-200 text-amber-900">Needs review</span>
              )}
            </div>
            {(expiryWarn(asset.registration_expiry, 'Registration') || expiryWarn(asset.insurance_expiry, 'Insurance')) && (
              <div data-testid="expiry-warning" className="mb-3 flex flex-col gap-1">
                {[expiryWarn(asset.registration_expiry, 'Registration'), expiryWarn(asset.insurance_expiry, 'Insurance')].filter(Boolean).map((w, i) => (
                  <div key={i} className={`text-xs font-semibold px-3 py-2 rounded-md ${w.includes('EXPIRED') ? 'bg-red-50 text-red-700 border border-red-200' : 'bg-amber-50 text-amber-800 border border-amber-200'}`}>
                    ⚠ {w}
                  </div>
                ))}
              </div>
            )}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div>
                <label className="block text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1">Asset Number</label>
                <input data-testid="detail-asset-number" value={detailsForm.asset_number} onChange={(e) => setDetailsForm({ ...detailsForm, asset_number: e.target.value })} placeholder="e.g. T-104" className="w-full px-2.5 py-1.5 text-sm border border-slate-300 rounded-md" />
              </div>
              <div>
                <label className="block text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1">Make</label>
                <input value={detailsForm.make} onChange={(e) => setDetailsForm({ ...detailsForm, make: e.target.value })} className="w-full px-2.5 py-1.5 text-sm border border-slate-300 rounded-md" />
              </div>
              <div>
                <label className="block text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1">Model</label>
                <input value={detailsForm.model} onChange={(e) => setDetailsForm({ ...detailsForm, model: e.target.value })} className="w-full px-2.5 py-1.5 text-sm border border-slate-300 rounded-md" />
              </div>
              <div>
                <label className="block text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1">Year</label>
                <input type="number" value={detailsForm.year} onChange={(e) => setDetailsForm({ ...detailsForm, year: e.target.value })} className="w-full px-2.5 py-1.5 text-sm border border-slate-300 rounded-md" />
              </div>
              <div className="col-span-2">
                <label className="block text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1">VIN</label>
                <input value={detailsForm.vin} onChange={(e) => setDetailsForm({ ...detailsForm, vin: e.target.value.toUpperCase() })} maxLength={17} className="w-full px-2.5 py-1.5 text-sm border border-slate-300 rounded-md" />
              </div>
              <div>
                <label className="block text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1">License Plate</label>
                <input value={detailsForm.license_plate} onChange={(e) => setDetailsForm({ ...detailsForm, license_plate: e.target.value.toUpperCase() })} className="w-full px-2.5 py-1.5 text-sm border border-slate-300 rounded-md" />
              </div>
              <div>
                <label className="block text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1">Plate State</label>
                <input value={detailsForm.license_plate_state} onChange={(e) => setDetailsForm({ ...detailsForm, license_plate_state: e.target.value.toUpperCase() })} maxLength={2} className="w-full px-2.5 py-1.5 text-sm border border-slate-300 rounded-md" />
              </div>
              <div>
                <label className="block text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1">Registration Expiry {asset.ai_detected_expiry?.registration_expiry && <span data-testid="ai-flag-registration" className="ml-1 text-[9px] px-1 py-0.5 rounded bg-violet-100 text-violet-700 normal-case">✨ AI · verify</span>}</label>
                <input data-testid="detail-registration-expiry" type="date" value={detailsForm.registration_expiry} onChange={(e) => setDetailsForm({ ...detailsForm, registration_expiry: e.target.value })} className="w-full px-2.5 py-1.5 text-sm border border-slate-300 rounded-md" />
              </div>
              <div>
                <label className="block text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1">Insurance Expiry {asset.ai_detected_expiry?.insurance_expiry && <span data-testid="ai-flag-insurance" className="ml-1 text-[9px] px-1 py-0.5 rounded bg-violet-100 text-violet-700 normal-case">✨ AI · verify</span>}</label>
                <input data-testid="detail-insurance-expiry" type="date" value={detailsForm.insurance_expiry} onChange={(e) => setDetailsForm({ ...detailsForm, insurance_expiry: e.target.value })} className="w-full px-2.5 py-1.5 text-sm border border-slate-300 rounded-md" />
              </div>
            </div>
            <p className="text-xs text-slate-500 mt-3" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
              Reassign the driver using the "Assign to" control above, or leave it on the driver who submitted it.
            </p>
            {Array.isArray(asset.documents) && asset.documents.length > 0 && (
              <div className="mt-4" data-testid="asset-app-documents">
                <p className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-2">Documents from application</p>
                <div className="flex flex-wrap gap-2">
                  {asset.documents.map((d) => (
                    <div key={d.doc_type} className="inline-flex items-center gap-1 border border-slate-300 rounded-md overflow-hidden">
                      <button
                        onClick={() => downloadAppDoc(d)}
                        data-testid={`app-doc-${d.doc_type}`}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-100"
                      >
                        <FileText className="w-4 h-4 text-[#002FA7]" />
                        {d.doc_type === 'truck_registration' ? 'Registration' : d.doc_type === 'physical_damage_insurance' ? 'Insurance' : d.file_name || d.doc_type}
                      </button>
                      {(d.doc_type === 'truck_registration' || d.doc_type === 'physical_damage_insurance') && (
                        <button
                          onClick={() => readExpiryAI(d)}
                          disabled={ocrDoc === d.doc_type}
                          data-testid={`ocr-expiry-${d.doc_type}`}
                          title="Read the expiry date off this document with AI"
                          className="px-2.5 py-1.5 text-xs font-semibold text-[#002FA7] border-l border-slate-300 hover:bg-[#002FA7]/5 disabled:opacity-50"
                        >
                          {ocrDoc === d.doc_type ? '…' : '✨ Read expiry'}
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
            <div className="flex items-center gap-2 mt-3">
              <button data-testid="detail-save-details" onClick={() => saveDetails(false)} disabled={savingDetails} className="px-4 py-2 rounded-md text-sm font-semibold border border-slate-300 text-slate-700 hover:bg-slate-100 disabled:opacity-50">
                Save details
              </button>
              {asset.pending_admin_review && (
                <button data-testid="detail-mark-reviewed" onClick={() => saveDetails(true)} disabled={savingDetails} className="px-4 py-2 rounded-md text-sm font-semibold bg-[#002FA7] text-white hover:bg-[#00247D] disabled:opacity-50">
                  Save &amp; mark reviewed
                </button>
              )}
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="flex items-center gap-1 border-b border-slate-200 mb-6 overflow-x-auto">
          {[
            { id: 'overview', label: 'Overview', icon: ClipboardCheck },
            { id: 'maintenance', label: `Maintenance${overdueCount + dueCount > 0 ? ` (${overdueCount + dueCount})` : ''}`, icon: Wrench },
            { id: 'history', label: 'Service History', icon: History },
            { id: 'fuel', label: 'Fuel & MPG', icon: Fuel },
            { id: 'odometer', label: 'Odometer Log', icon: Gauge },
            { id: 'assignments', label: 'Assignment History', icon: User },
          ].map(t => {
            const Icon = t.icon;
            return (
              <button
                key={t.id}
                data-testid={`tab-${t.id}`}
                onClick={() => setTab(t.id)}
                className={`flex items-center gap-2 px-4 py-2.5 text-sm font-semibold border-b-2 transition-all whitespace-nowrap ${tab === t.id ? 'border-[#002FA7] text-[#002FA7]' : 'border-transparent text-slate-600 hover:text-slate-900'}`}
                style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
              >
                <Icon className="w-4 h-4" />
                {t.label}
              </button>
            );
          })}
        </div>

        {/* Tab content */}
        {tab === 'overview' && (
          <div className="space-y-4">
            {asset.asset_type === 'truck' && (
              <>
                <ELDLinkControls
                  asset={asset}
                  isAdmin={isAdmin}
                  onLinked={fetchAll}
                />
                <DualLocationCard truckId={asset.id} />
              </>
            )}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <DetailCard title="Identification" rows={[
                ['Type', asset.asset_type],
                ['Serial / VIN', asset.serial_number],
                ['Make', asset.make],
                ['Model', asset.model],
                ['Year', asset.year],
              ]} />
              <DetailCard title="Vehicle Info" rows={[
                ['License Plate', asset.license_plate],
                ['VIN', asset.vin],
                ['Registration Expires', fmtDate(asset.registration_expiry)],
                ['Insurance Expires', fmtDate(asset.insurance_expiry)],
              ]} hidden={asset.asset_type !== 'truck' && asset.asset_type !== 'trailer'} />
              <DetailCard title="Device Info" rows={[
                ['IMEI / MAC', asset.imei_or_mac],
                ['Provider', asset.provider],
                ['Plan', asset.plan],
              ]} hidden={!['gps', 'eld', 'tablet'].includes(asset.asset_type)} />
              <DetailCard title="Notes" rows={[['Notes', asset.notes]]} hidden={!asset.notes} />
            </div>
          </div>
        )}

        {tab === 'maintenance' && (
          <div>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Work Sans, sans-serif' }}>Maintenance Schedule</h2>
              {isAdmin && (
                <button data-testid="add-rule-button" onClick={() => setShowRuleForm(!showRuleForm)} className="text-sm bg-[#002FA7] text-white px-3 py-1.5 rounded-md font-semibold hover:bg-[#00247D] flex items-center gap-1" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                  <Plus className="w-3.5 h-3.5" /> Add Rule
                </button>
              )}
            </div>
            {showRuleForm && (
              <form onSubmit={handleCreateRule} className="bg-slate-50 border border-slate-200 rounded-md p-4 mb-4 grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="rule-form">
                <div className="md:col-span-2">
                  <label className="block text-xs font-semibold text-slate-600 mb-1">Service Name *</label>
                  <input data-testid="rule-name" required value={ruleForm.name} onChange={(e) => setRuleForm({ ...ruleForm, name: e.target.value })} placeholder="Oil Change, Tire Rotation, Brake Inspection..." className="w-full px-3 py-2 border border-slate-200 rounded-md text-sm" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-600 mb-1">Interval (miles)</label>
                  <input data-testid="rule-interval-miles" type="number" value={ruleForm.interval_miles} onChange={(e) => setRuleForm({ ...ruleForm, interval_miles: e.target.value })} className="w-full px-3 py-2 border border-slate-200 rounded-md text-sm" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-600 mb-1">Interval (days)</label>
                  <input type="number" value={ruleForm.interval_days} onChange={(e) => setRuleForm({ ...ruleForm, interval_days: e.target.value })} className="w-full px-3 py-2 border border-slate-200 rounded-md text-sm" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-600 mb-1">Last performed at (miles)</label>
                  <input type="number" value={ruleForm.last_performed_miles} onChange={(e) => setRuleForm({ ...ruleForm, last_performed_miles: e.target.value })} className="w-full px-3 py-2 border border-slate-200 rounded-md text-sm" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-600 mb-1">Last performed on</label>
                  <input type="date" value={ruleForm.last_performed_at} onChange={(e) => setRuleForm({ ...ruleForm, last_performed_at: e.target.value })} className="w-full px-3 py-2 border border-slate-200 rounded-md text-sm" />
                </div>
                <div className="md:col-span-2 flex justify-end gap-2">
                  <button type="button" onClick={() => setShowRuleForm(false)} className="px-3 py-1.5 text-sm text-slate-700">Cancel</button>
                  <button data-testid="submit-rule" type="submit" className="px-4 py-1.5 bg-[#002FA7] text-white rounded-md text-sm font-semibold">Add Rule</button>
                </div>
              </form>
            )}
            {rules.length === 0 ? (
              <p className="text-sm text-slate-500 bg-white border border-dashed border-slate-200 rounded-md p-6 text-center" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>No maintenance rules yet.</p>
            ) : (
              <div className="space-y-2">
                {rules.map(r => {
                  const s = computeRuleStatus(r);
                  const tone = s.isOverdue ? 'border-red-300 bg-red-50' : s.isDue ? 'border-amber-300 bg-amber-50' : 'border-slate-200 bg-white';
                  return (
                    <div key={r.id} data-testid={`rule-${r.id}`} className={`border rounded-md p-4 flex items-center justify-between gap-4 ${tone}`}>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          {s.isOverdue ? <AlertTriangle className="w-4 h-4 text-red-600" /> : s.isDue ? <AlertTriangle className="w-4 h-4 text-amber-600" /> : <CheckCircle className="w-4 h-4 text-green-600" />}
                          <p className="font-semibold text-slate-900" style={{ fontFamily: 'Work Sans, sans-serif' }}>{r.name}</p>
                          {r.is_default && <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-slate-200 text-slate-600 uppercase tracking-wider">default</span>}
                        </div>
                        <div className="text-xs text-slate-600 flex flex-wrap gap-x-4 gap-y-1" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                          {r.interval_miles && <span>Every {r.interval_miles.toLocaleString()} mi</span>}
                          {r.interval_days && <span>Every {r.interval_days} days</span>}
                          {r.last_performed_miles != null && <span>Last @ {r.last_performed_miles.toLocaleString()} mi</span>}
                          {r.last_performed_at && <span>Last on {fmtDate(r.last_performed_at)}</span>}
                          {s.milesRemaining !== null && (
                            <span className={s.isOverdue ? 'text-red-700 font-semibold' : s.isDue ? 'text-amber-700 font-semibold' : ''}>
                              {s.isOverdue ? `${Math.abs(Math.round(s.milesRemaining))} mi overdue` : `${Math.round(s.milesRemaining).toLocaleString()} mi until next`}
                            </span>
                          )}
                          {s.daysRemaining !== null && (
                            <span className={s.isOverdue ? 'text-red-700 font-semibold' : s.isDue ? 'text-amber-700 font-semibold' : ''}>
                              {s.daysRemaining < 0 ? `${Math.abs(s.daysRemaining)} days overdue` : `${s.daysRemaining} days until next`}
                            </span>
                          )}
                        </div>
                      </div>
                      {isAdmin && (
                        <div className="flex items-center gap-1">
                          <button
                            data-testid={`log-rule-${r.id}`}
                            onClick={() => { setLogForm({ ...logForm, description: r.name, rule_id: r.id, performed_at_miles: String(Math.round(asset.current_odometer || 0)) }); setShowLogForm(true); setTab('history'); }}
                            className="text-xs px-2 py-1 bg-white border border-slate-300 rounded text-slate-700 hover:border-[#002FA7] hover:text-[#002FA7]"
                            style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
                          >
                            Log Service
                          </button>
                          <button onClick={() => handleDeleteRule(r.id)} className="p-1.5 text-red-600 hover:bg-red-100 rounded" data-testid={`delete-rule-${r.id}`}>
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {tab === 'history' && (
          <div>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Work Sans, sans-serif' }}>Service History</h2>
              {isAdmin && (
                <button data-testid="add-log-button" onClick={() => setShowLogForm(!showLogForm)} className="text-sm bg-[#002FA7] text-white px-3 py-1.5 rounded-md font-semibold hover:bg-[#00247D] flex items-center gap-1" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                  <Plus className="w-3.5 h-3.5" /> Log Service
                </button>
              )}
            </div>
            {showLogForm && (
              <form onSubmit={handleCreateLog} className="bg-slate-50 border border-slate-200 rounded-md p-4 mb-4 grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="log-form">
                <div className="md:col-span-2">
                  <label className="block text-xs font-semibold text-slate-600 mb-1">Description *</label>
                  <input data-testid="log-description" required value={logForm.description} onChange={(e) => setLogForm({ ...logForm, description: e.target.value })} className="w-full px-3 py-2 border border-slate-200 rounded-md text-sm" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-600 mb-1">Performed at (miles)</label>
                  <input data-testid="log-miles" type="number" value={logForm.performed_at_miles} onChange={(e) => setLogForm({ ...logForm, performed_at_miles: e.target.value })} className="w-full px-3 py-2 border border-slate-200 rounded-md text-sm" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-600 mb-1">Cost ($)</label>
                  <input type="number" step="0.01" value={logForm.cost} onChange={(e) => setLogForm({ ...logForm, cost: e.target.value })} className="w-full px-3 py-2 border border-slate-200 rounded-md text-sm" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-600 mb-1">Vendor</label>
                  <input value={logForm.vendor} onChange={(e) => setLogForm({ ...logForm, vendor: e.target.value })} className="w-full px-3 py-2 border border-slate-200 rounded-md text-sm" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-600 mb-1">Linked Rule</label>
                  <select value={logForm.rule_id} onChange={(e) => setLogForm({ ...logForm, rule_id: e.target.value })} className="w-full px-3 py-2 border border-slate-200 rounded-md text-sm">
                    <option value="">— None —</option>
                    {rules.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
                  </select>
                </div>
                <div className="md:col-span-2">
                  <label className="block text-xs font-semibold text-slate-600 mb-1">Notes</label>
                  <textarea rows="2" value={logForm.notes} onChange={(e) => setLogForm({ ...logForm, notes: e.target.value })} className="w-full px-3 py-2 border border-slate-200 rounded-md text-sm" />
                </div>
                <div className="md:col-span-2 flex justify-end gap-2">
                  <button type="button" onClick={() => setShowLogForm(false)} className="px-3 py-1.5 text-sm text-slate-700">Cancel</button>
                  <button data-testid="submit-log" type="submit" className="px-4 py-1.5 bg-[#002FA7] text-white rounded-md text-sm font-semibold">Save</button>
                </div>
              </form>
            )}
            {logs.length === 0 ? (
              <p className="text-sm text-slate-500 bg-white border border-dashed border-slate-200 rounded-md p-6 text-center" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>No service history yet.</p>
            ) : (
              <div className="bg-white border border-slate-200 rounded-md divide-y divide-slate-100">
                {logs.map(l => (
                  <div key={l.id} data-testid={`log-${l.id}`} className="p-4">
                    <div className="flex items-start justify-between gap-3 mb-1">
                      <p className="font-semibold text-slate-900" style={{ fontFamily: 'Work Sans, sans-serif' }}>{l.description}</p>
                      {l.cost != null && <span className="text-sm font-semibold text-slate-900" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>${l.cost.toFixed(2)}</span>}
                    </div>
                    <div className="text-xs text-slate-600 flex flex-wrap gap-x-4 gap-y-1" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                      <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />{fmtDT(l.performed_at)}</span>
                      {l.performed_at_miles != null && <span>@ {l.performed_at_miles.toLocaleString()} mi</span>}
                      {l.vendor && <span>by {l.vendor}</span>}
                    </div>
                    {l.notes && <p className="text-xs text-slate-600 mt-2" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>{l.notes}</p>}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {tab === 'fuel' && (
          <div>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Work Sans, sans-serif' }}>Fuel & MPG</h2>
              <button data-testid="add-fuel-button" onClick={() => setShowFuelForm(!showFuelForm)} className="text-sm bg-[#002FA7] text-white px-3 py-1.5 rounded-md font-semibold hover:bg-[#00247D] flex items-center gap-1" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                <Plus className="w-3.5 h-3.5" /> Log Fill-up
              </button>
            </div>

            {/* Summary cards */}
            {fuelSummary && fuelSummary.fill_ups > 0 && (
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-5" data-testid="fuel-summary">
                <SummaryCard label="Fill-ups (90d)" value={fuelSummary.fill_ups} />
                <SummaryCard label="Gallons" value={`${fuelSummary.total_gallons.toLocaleString()} gal`} />
                <SummaryCard label="Total Cost" value={fuelSummary.total_cost ? `$${fuelSummary.total_cost.toLocaleString()}` : '—'} />
                <SummaryCard label="Avg MPG" value={fuelSummary.avg_mpg ? fuelSummary.avg_mpg.toFixed(2) : '—'} highlight />
                <SummaryCard label="Avg $/gal" value={fuelSummary.avg_price_per_gallon ? `$${fuelSummary.avg_price_per_gallon.toFixed(3)}` : '—'} />
              </div>
            )}

            {showFuelForm && (
              <form onSubmit={handleCreateFuel} className="bg-slate-50 border border-slate-200 rounded-md p-4 mb-4 grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="fuel-form">
                <div>
                  <label className="block text-xs font-semibold text-slate-600 mb-1">Odometer (mi) *</label>
                  <input data-testid="fuel-odometer" required type="number" min="0" step="1" value={fuelForm.odometer} onChange={(e) => setFuelForm({ ...fuelForm, odometer: e.target.value })} className="w-full px-3 py-2 border border-slate-200 rounded-md text-sm" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-600 mb-1">Gallons *</label>
                  <input data-testid="fuel-gallons" required type="number" min="0.01" step="0.01" value={fuelForm.gallons} onChange={(e) => setFuelForm({ ...fuelForm, gallons: e.target.value })} className="w-full px-3 py-2 border border-slate-200 rounded-md text-sm" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-600 mb-1">Price / gallon ($)</label>
                  <input data-testid="fuel-price" type="number" step="0.001" value={fuelForm.price_per_gallon} onChange={(e) => setFuelForm({ ...fuelForm, price_per_gallon: e.target.value })} className="w-full px-3 py-2 border border-slate-200 rounded-md text-sm" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-600 mb-1">Station</label>
                  <input value={fuelForm.station} onChange={(e) => setFuelForm({ ...fuelForm, station: e.target.value })} placeholder="Pilot, Flying J, Love's..." className="w-full px-3 py-2 border border-slate-200 rounded-md text-sm" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-600 mb-1">Fuel Type</label>
                  <select value={fuelForm.fuel_type} onChange={(e) => setFuelForm({ ...fuelForm, fuel_type: e.target.value })} className="w-full px-3 py-2 border border-slate-200 rounded-md text-sm">
                    <option value="diesel">Diesel</option>
                    <option value="gas">Gas</option>
                    <option value="def">DEF</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                <div className="flex items-end">
                  <label className="flex items-center gap-2 px-3 py-2 text-sm">
                    <input data-testid="fuel-full-tank" type="checkbox" checked={fuelForm.is_full_tank} onChange={(e) => setFuelForm({ ...fuelForm, is_full_tank: e.target.checked })} className="w-4 h-4 accent-[#002FA7]" />
                    <span className="text-slate-700" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Full tank fill-up (computes MPG)</span>
                  </label>
                </div>
                <div className="md:col-span-2">
                  <label className="block text-xs font-semibold text-slate-600 mb-1">Notes</label>
                  <textarea rows="2" value={fuelForm.notes} onChange={(e) => setFuelForm({ ...fuelForm, notes: e.target.value })} className="w-full px-3 py-2 border border-slate-200 rounded-md text-sm" />
                </div>
                <div className="md:col-span-2 flex justify-end gap-2">
                  <button type="button" onClick={() => setShowFuelForm(false)} className="px-3 py-1.5 text-sm text-slate-700">Cancel</button>
                  <button data-testid="submit-fuel" type="submit" className="px-4 py-1.5 bg-[#002FA7] text-white rounded-md text-sm font-semibold">Save</button>
                </div>
              </form>
            )}

            {fuelLogs.length === 0 ? (
              <p className="text-sm text-slate-500 bg-white border border-dashed border-slate-200 rounded-md p-6 text-center" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>No fuel records yet.</p>
            ) : (
              <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
                <table className="w-full text-sm" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="text-left px-4 py-2 text-xs font-semibold uppercase tracking-widest text-slate-600">When</th>
                      <th className="text-right px-4 py-2 text-xs font-semibold uppercase tracking-widest text-slate-600">Odo</th>
                      <th className="text-right px-4 py-2 text-xs font-semibold uppercase tracking-widest text-slate-600">Gallons</th>
                      <th className="text-right px-4 py-2 text-xs font-semibold uppercase tracking-widest text-slate-600">$/gal</th>
                      <th className="text-right px-4 py-2 text-xs font-semibold uppercase tracking-widest text-slate-600">Cost</th>
                      <th className="text-right px-4 py-2 text-xs font-semibold uppercase tracking-widest text-slate-600">MPG</th>
                      <th className="text-left px-4 py-2 text-xs font-semibold uppercase tracking-widest text-slate-600">Station</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {fuelLogs.map(f => (
                      <tr key={f.id} data-testid={`fuel-${f.id}`}>
                        <td className="px-4 py-2 text-slate-700">{fmtDT(f.recorded_at)}</td>
                        <td className="px-4 py-2 text-right text-slate-700">{Math.round(f.odometer).toLocaleString()}</td>
                        <td className="px-4 py-2 text-right text-slate-700">{f.gallons.toFixed(2)}</td>
                        <td className="px-4 py-2 text-right text-slate-700">{f.price_per_gallon ? `$${f.price_per_gallon.toFixed(3)}` : '—'}</td>
                        <td className="px-4 py-2 text-right text-slate-900 font-medium">{f.total_cost ? `$${f.total_cost.toFixed(2)}` : '—'}</td>
                        <td className="px-4 py-2 text-right font-semibold text-slate-900">{f.mpg ? f.mpg.toFixed(2) : '—'}</td>
                        <td className="px-4 py-2 text-slate-600 truncate max-w-[160px]">{f.station || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}


        {tab === 'odometer' && (
          <div>
            <h2 className="text-lg font-bold tracking-tight text-slate-900 mb-4" style={{ fontFamily: 'Work Sans, sans-serif' }}>Odometer Readings</h2>
            {odoLogs.length === 0 ? (
              <p className="text-sm text-slate-500 bg-white border border-dashed border-slate-200 rounded-md p-6 text-center" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>No odometer readings yet.</p>
            ) : (
              <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
                <table className="w-full text-sm" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="text-left px-4 py-2 text-xs font-semibold uppercase tracking-widest text-slate-600">When</th>
                      <th className="text-left px-4 py-2 text-xs font-semibold uppercase tracking-widest text-slate-600">Type</th>
                      <th className="text-right px-4 py-2 text-xs font-semibold uppercase tracking-widest text-slate-600">Reading</th>
                      <th className="text-left px-4 py-2 text-xs font-semibold uppercase tracking-widest text-slate-600">Issues</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {odoLogs.map(o => (
                      <tr key={o.id} data-testid={`odo-${o.id}`}>
                        <td className="px-4 py-2 text-slate-700">{fmtDT(o.recorded_at)}</td>
                        <td className="px-4 py-2"><span className={`text-[10px] font-semibold px-2 py-0.5 rounded uppercase tracking-wider ${o.log_type === 'pre_trip' ? 'bg-blue-100 text-blue-700' : o.log_type === 'post_trip' ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-600'}`}>{o.log_type.replace('_', '-')}</span></td>
                        <td className="px-4 py-2 text-right font-semibold text-slate-900">{Math.round(o.reading).toLocaleString()} mi</td>
                        <td className="px-4 py-2 text-slate-600 truncate max-w-xs">{o.issues || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {tab === 'assignments' && (
          <div>
            <h2 className="text-lg font-bold tracking-tight text-slate-900 mb-4" style={{ fontFamily: 'Work Sans, sans-serif' }}>Assignment History</h2>
            {assignments.length === 0 ? (
              <p className="text-sm text-slate-500 bg-white border border-dashed border-slate-200 rounded-md p-6 text-center" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>No assignment history yet.</p>
            ) : (
              <div className="bg-white border border-slate-200 rounded-md divide-y divide-slate-100">
                {assignments.map(a => (
                  <div key={a.id} className="p-4 flex items-center justify-between">
                    <div>
                      <p className="font-semibold text-slate-900" style={{ fontFamily: 'Work Sans, sans-serif' }}>{a.driver_name}</p>
                      <p className="text-xs text-slate-600 mt-0.5" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                        {fmtDate(a.assigned_at)} → {a.unassigned_at ? fmtDate(a.unassigned_at) : <span className="text-green-700 font-semibold">Current</span>}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

const DetailCard = ({ title, rows, hidden }) => {
  if (hidden) return null;
  const visible = rows.filter(([, v]) => v !== null && v !== undefined && v !== '' && v !== '—');
  if (visible.length === 0) return null;
  return (
    <div className="bg-white border border-slate-200 rounded-md p-5">
      <h3 className="text-sm font-bold uppercase tracking-widest text-slate-700 mb-3" style={{ fontFamily: 'Work Sans, sans-serif' }}>{title}</h3>
      <dl className="space-y-2 text-sm" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
        {visible.map(([k, v]) => (
          <div key={k} className="flex justify-between gap-3">
            <dt className="text-slate-500 truncate">{k}</dt>
            <dd className="text-slate-900 font-medium truncate">{v}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
};

const SummaryCard = ({ label, value, highlight }) => (
  <div className={`bg-white border rounded-md p-3 ${highlight ? 'border-[#002FA7]/40' : 'border-slate-200'}`}>
    <p className="text-[10px] font-semibold uppercase tracking-widest text-slate-500" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>{label}</p>
    <p className={`mt-1 font-bold ${highlight ? 'text-[#002FA7] text-xl' : 'text-slate-900 text-base'}`} style={{ fontFamily: 'Work Sans, sans-serif' }}>{value}</p>
  </div>
);

// ELD provider link management — companion to DualLocationCard.
// Company admins can 1-click connect a mock ELD (for demos) or connect a
// real provider by entering a device id. Dispatch/drivers only see the
// read-only status pill. Never blocks or affects the phone GPS system.
const ELDLinkControls = ({ asset, isAdmin, onLinked }) => {
  const [providers, setProviders] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [provider, setProvider] = useState('mock');
  const [deviceId, setDeviceId] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [showCreds, setShowCreds] = useState(false);

  useEffect(() => {
    if (!isAdmin) return;
    axios.get(`${API}/eld/providers`, { withCredentials: true })
      .then((r) => setProviders(Array.isArray(r.data) ? r.data : []))
      .catch(() => setProviders([]));
  }, [isAdmin]);

  const linked = !!asset.eld_device_id;

  const handleLink = async () => {
    setBusy(true);
    setErr(null);
    try {
      await axios.post(
        `${API}/eld/link`,
        {
          truck_asset_id: asset.id,
          provider,
          device_id: deviceId ? deviceId.trim() : undefined,
        },
        { withCredentials: true },
      );
      // Immediately kick a refresh so the DualLocationCard has data on
      // first render. Best-effort.
      axios.post(`${API}/eld/refresh`, {}, { withCredentials: true }).catch(() => {});
      setShowForm(false);
      setDeviceId('');
      if (typeof onLinked === 'function') onLinked();
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message || 'Failed to link');
    } finally {
      setBusy(false);
    }
  };

  const handleUnlink = async () => {
    if (!window.confirm('Unlink this ELD device? Phone GPS will keep working.')) return;
    setBusy(true);
    try {
      await axios.post(
        `${API}/eld/unlink`,
        { truck_asset_id: asset.id },
        { withCredentials: true },
      );
      if (typeof onLinked === 'function') onLinked();
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message || 'Failed to unlink');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      data-testid="eld-link-controls"
      className="bg-white border border-slate-200 rounded-md p-4 flex items-center justify-between gap-3 flex-wrap"
    >
      <div className="flex items-center gap-3 min-w-0">
        <div className={`w-9 h-9 rounded-md flex items-center justify-center ${
          linked ? 'bg-[#002FA7] text-white' : 'bg-slate-100 text-slate-500'
        }`}>
          <Satellite className="w-4 h-4" strokeWidth={2.25} />
        </div>
        <div className="min-w-0">
          <div className="text-sm font-bold text-slate-900" style={{ fontFamily: 'Work Sans, sans-serif' }}>
            ELD Device
          </div>
          {linked ? (
            <div className="text-xs text-slate-600" data-testid="eld-linked-label">
              Provider: <span className="font-semibold capitalize">{asset.eld_provider}</span>{' '}
              · Device: <span className="font-mono">{asset.eld_device_id}</span>
            </div>
          ) : (
            <div className="text-xs text-slate-500">
              Not linked. Phone GPS continues working independently.
            </div>
          )}
        </div>
      </div>
      {isAdmin && !linked && !showForm && (
        <div className="flex gap-2">
          <button
            type="button"
            data-testid="eld-connect-mock"
            onClick={async () => {
              setProvider('mock');
              setDeviceId('');
              // 1-click mock link — no form step needed.
              await handleLink();
            }}
            disabled={busy}
            className="text-xs bg-[#002FA7] text-white px-3 py-1.5 rounded-md font-semibold hover:bg-[#00247D] disabled:opacity-50"
            style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
          >
            Connect Demo ELD
          </button>
          <button
            type="button"
            data-testid="eld-connect-provider"
            onClick={() => setShowForm(true)}
            className="text-xs border border-slate-300 text-slate-700 px-3 py-1.5 rounded-md font-semibold hover:bg-slate-50"
            style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
          >
            Connect real ELD…
          </button>
        </div>
      )}
      {isAdmin && !linked && showForm && (
        <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto">
          <select
            data-testid="eld-provider-select"
            value={provider}
            onChange={(e) => { setProvider(e.target.value); setErr(null); }}
            className="px-2 py-1.5 border border-slate-300 rounded-md text-xs bg-white"
          >
            {providers.map((p) => (
              <option key={p.key} value={p.key} disabled={p.key !== 'mock' && !p.configured}>
                {p.label}{p.key !== 'mock' && !p.configured ? ' (needs API key)' : ''}
              </option>
            ))}
          </select>
          <input
            data-testid="eld-device-id-input"
            placeholder="Device ID / IMEI"
            value={deviceId}
            onChange={(e) => setDeviceId(e.target.value)}
            className="px-2 py-1.5 border border-slate-300 rounded-md text-xs w-40"
          />
          <button
            type="button"
            data-testid="eld-link-submit"
            onClick={handleLink}
            disabled={busy}
            className="text-xs bg-[#002FA7] text-white px-3 py-1.5 rounded-md font-semibold hover:bg-[#00247D] disabled:opacity-50"
          >
            Link
          </button>
          <button
            type="button"
            data-testid="eld-manage-credentials"
            onClick={() => setShowCreds(true)}
            className="text-xs underline text-[#002FA7]"
          >
            Manage API keys
          </button>
          <button
            type="button"
            onClick={() => { setShowForm(false); setErr(null); }}
            className="text-xs text-slate-500 px-2 py-1.5"
          >
            Cancel
          </button>
        </div>
      )}
      {isAdmin && showCreds && (
        <ELDCredentialsModal
          providers={providers}
          onClose={(refresh) => {
            setShowCreds(false);
            if (refresh) {
              // Re-pull providers so newly-configured ones become selectable.
              axios.get(`${API}/eld/providers`, { withCredentials: true })
                .then((r) => setProviders(Array.isArray(r.data) ? r.data : []))
                .catch(() => {});
            }
          }}
        />
      )}
      {isAdmin && linked && (
        <button
          type="button"
          data-testid="eld-unlink"
          onClick={handleUnlink}
          disabled={busy}
          className="text-xs border border-red-200 text-red-700 px-3 py-1.5 rounded-md font-semibold hover:bg-red-50 disabled:opacity-50"
        >
          Unlink
        </button>
      )}
      {err && (
        <div className="text-xs text-red-600 w-full" data-testid="eld-link-error">
          {err}
        </div>
      )}
    </div>
  );
};

export default AssetDetail;

const ELDCredentialsModal = ({ providers, onClose }) => {
  const realProviders = (providers || []).filter((p) => p.key !== 'mock');
  const [selected, setSelected] = useState(realProviders[0]?.key || 'hive');
  const [apiKey, setApiKey] = useState('');
  const [baseUrl, setBaseUrl] = useState('');
  const [vehiclesPath, setVehiclesPath] = useState('');
  const [authMode, setAuthMode] = useState('x-api-key');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const [changed, setChanged] = useState(false);

  const current = realProviders.find((p) => p.key === selected);
  // Hive has no public API contract — we let admins paste every field their
  // Hive support gives them. Motive uses a fixed URL + X-API-Key so those
  // rows stay hidden unless the admin explicitly picks Hive.
  const needsExtraConfig = selected === 'hive';

  const save = async () => {
    setBusy(true); setMsg(null);
    try {
      const body = { provider: selected, api_key: apiKey || null };
      if (needsExtraConfig) {
        if (baseUrl) body.base_url = baseUrl;
        if (vehiclesPath) body.vehicles_path = vehiclesPath;
        if (authMode) body.auth_mode = authMode;
      }
      await axios.post(`${API}/eld/credentials`, body, { withCredentials: true });
      setMsg(apiKey ? 'Saved — provider now selectable.' : 'Cleared credentials for provider.');
      setApiKey('');
      setChanged(true);
    } catch (e) {
      setMsg(e?.response?.data?.detail || e.message || 'Save failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      data-testid="eld-credentials-modal"
      className="fixed inset-0 z-[60] bg-slate-900/50 flex items-center justify-center p-4"
      onClick={() => onClose(changed)}
    >
      <div
        className="w-full max-w-md bg-white rounded-md shadow-xl p-5"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="text-base font-bold text-slate-900 mb-1" style={{ fontFamily: 'Work Sans, sans-serif' }}>
          Manage ELD API keys
        </h3>
        <p className="text-xs text-slate-500 mb-4">
          Stored on your company. Poller uses this key to fetch live telemetry.
        </p>
        <label className="text-[10px] uppercase tracking-widest font-semibold text-slate-500">Provider</label>
        <select
          data-testid="eld-creds-provider"
          value={selected}
          onChange={(e) => { setSelected(e.target.value); setApiKey(''); setMsg(null); }}
          className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm bg-white mt-1 mb-3"
        >
          {realProviders.map((p) => (
            <option key={p.key} value={p.key}>{p.label}{p.configured ? ' ✓ configured' : ''}</option>
          ))}
        </select>
        <label className="text-[10px] uppercase tracking-widest font-semibold text-slate-500">API Key</label>
        <input
          data-testid="eld-creds-apikey"
          type="password"
          autoComplete="off"
          value={apiKey}
          onChange={(e) => setApiKey(e.target.value)}
          placeholder={current?.configured ? '(configured — enter a new key to rotate)' : 'Paste your provider API key'}
          className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm mt-1"
        />
        {needsExtraConfig && (
          <div className="mt-3 grid grid-cols-1 gap-3">
            <div>
              <label className="text-[10px] uppercase tracking-widest font-semibold text-slate-500">
                Base URL <span className="text-slate-400 normal-case font-normal">(from Hive support)</span>
              </label>
              <input
                data-testid="eld-creds-base-url"
                value={baseUrl}
                onChange={(e) => setBaseUrl(e.target.value)}
                placeholder="https://api.hiveeld.com"
                className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm mt-1"
              />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest font-semibold text-slate-500">
                Vehicles endpoint path
              </label>
              <input
                data-testid="eld-creds-path"
                value={vehiclesPath}
                onChange={(e) => setVehiclesPath(e.target.value)}
                placeholder="/vehicles/current"
                className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm mt-1"
              />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest font-semibold text-slate-500">
                Auth header
              </label>
              <select
                data-testid="eld-creds-auth-mode"
                value={authMode}
                onChange={(e) => setAuthMode(e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm bg-white mt-1"
              >
                <option value="x-api-key">X-API-Key header</option>
                <option value="bearer">Authorization: Bearer</option>
              </select>
            </div>
          </div>
        )}
        <div className="text-[11px] text-slate-500 mt-2">
          {selected === 'motive' && 'For Motive: Fleet Dashboard → Admin → Developers → API Access → Request API Key.'}
          {selected === 'hive' && 'For Hive: contact Hive/VFS fleet support for your API key + base URL + endpoint path.'}
        </div>
        {msg && (
          <div data-testid="eld-creds-msg" className="text-xs text-slate-700 mt-3 bg-slate-50 border border-slate-200 rounded p-2">
            {msg}
          </div>
        )}
        <div className="flex justify-end gap-2 mt-4">
          <button
            type="button"
            data-testid="eld-creds-clear"
            onClick={async () => {
              setApiKey('');
              setBusy(true);
              try {
                await axios.post(`${API}/eld/credentials`, { provider: selected, api_key: null }, { withCredentials: true });
                setMsg('Credentials cleared.');
                setChanged(true);
              } catch (e) {
                setMsg(e?.response?.data?.detail || e.message);
              } finally { setBusy(false); }
            }}
            disabled={busy || !current?.configured}
            className="text-xs px-3 py-1.5 rounded-md border border-red-200 text-red-700 hover:bg-red-50 disabled:opacity-50"
          >
            Clear
          </button>
          <button
            type="button"
            data-testid="eld-creds-save"
            onClick={save}
            disabled={busy || !apiKey}
            className="text-xs bg-[#002FA7] text-white px-3 py-1.5 rounded-md font-semibold hover:bg-[#00247D] disabled:opacity-50"
          >
            Save
          </button>
          <button
            type="button"
            onClick={() => onClose(changed)}
            className="text-xs text-slate-600 px-3 py-1.5"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};
